/**
 * Attributes:
 *   group | what group this tab belongs to? | example: `tabs`
 *   id | html ID, required for ADA/aria labels | example: `tab-title-1`
 *   arai-controls | html ID of the toggler's target | example: `tab-content-1`
 *   initially-active | true or false, a group should only have 1 ada-toggler set to true
 *   type | accordion|tab, this changes the functionality and aira-labels a bit
 *
 * All functionality here is based on these examples:
 * - https://www.w3.org/WAI/ARIA/apg/patterns/tabs/examples/tabs-automatic/
 * - https://www.w3.org/WAI/ARIA/apg/patterns/accordion/examples/accordion/
 */
{
  class adaToggler extends HTMLButtonElement {
    constructor(){
      super()
      this.loaded = false
    }

    _isWindowHashInthisGroup(){
      return Array.from(document.querySelectorAll(`button[group="${this.getAttribute('group')}"]`)).filter(b => b.id == window.location.hash.replace('#', '')).length
    }

    connectedCallback() {
      if(this.loaded){
        return false
      } else {
        this.loaded = true
      }

      // admin preview hack fix because gutenberg doesn't like custom elements
      if(this.hasAttribute('className')){
        this.classList = this.getAttribute('className')
        this.removeAttribute('className')
      }      
      
      // set up some defaults
      if(!this.getAttribute('type')){
        this.setAttribute('type', 'accordion')
      }

      if(this.getAttribute('type') !== 'accordion' && this.getAttribute('type') !== 'tab'){
        console.warn("Invalid type on ADA Toggler ", this)
      }

      if(this.getAttribute('type') == 'accordion'){
        if(!this.hasAttribute('slide-duration')){
          this.setAttribute('slide-duration', 500)
        }
      }

      if(!this.id){
        console.warn('ADA toggler should have an ID')
      }

      if(!this.closest('[role="tablist"]') && this.getAttribute('type') == 'tab'){
        console.warn('ADA toggler tab should be a child of a node with `[role="tablist"]`', this)
      }
      
      if(!this.getAttribute('group')){
        this.setAttribute('group', "_" + Math.random().toString(16).slice(2))
      }

      if (!this.hasAttribute('push-history')) {
        this.setAttribute('push-history', 'false');
      }

      this.targetElement = document.querySelector('#' + this.getAttribute('aria-controls'))

      if(!this.targetElement){
        console.warn('No target for ada toggler ', this)
        return
      }

      // the target element is focusable because we need to focus something and content within the target may not be focusable
      if(!this.targetElement.hasAttribute('tabindex')){
        this.targetElement.setAttribute('tabindex', 0)
      }

      //set up default aria tags
      let isInitiallyActive = this.getAttribute('initially-active') == 'true'

      if(this._isWindowHashInthisGroup()){
        isInitiallyActive = window.location.hash.replace('#', '') == this.id
      }

      if(!isInitiallyActive){
        this.targetElement.style.display = 'none'
        this.querySelectorAll(`[ada-toggler--expanded]`).forEach(el => el.style.display = "")
        this.querySelectorAll(`[ada-toggler--collapsed]`).forEach(el => el.style.display = "none")        
      } else {
        this.targetElement.style.display = ''
        this.querySelectorAll(`[ada-toggler--expanded]`).forEach(el => el.style.display = "none")
        this.querySelectorAll(`[ada-toggler--collapsed]`).forEach(el => el.style.display = "")
      }

      if(this.getAttribute('type') == 'accordion'){
        this.setAttribute('aria-expanded', isInitiallyActive)
        this.targetElement.setAttribute('aria-labelledby', this.id)
        this.targetElement.setAttribute('role', 'region')
      } else if(this.getAttribute('type') == 'tab'){
        this.setAttribute('role', 'tab')
        this.setAttribute('aria-selected', isInitiallyActive)
        if(!isInitiallyActive){
          this.setAttribute('tabindex', -1)
        }
        this.targetElement.setAttribute('aria-labelledby', this.id)
        this.targetElement.setAttribute('role', 'tabpanel')
      } 

      // Default to click if trigger type not set
      if(!this.hasAttribute('trigger-type')){
        this.setAttribute('trigger-type', 'click')
      }

      const triggerType = this.getAttribute('trigger-type')

      if(triggerType === 'hover'){
        this.addEventListener('mouseenter', e => {
          this._onTogglerClick(true)
        })
      } else {
        this.addEventListener('click', e => {
          e.preventDefault()
          this._onTogglerClick(true)
        })
      }


      this.addEventListener('keydown', e => {
        if(this.getAttribute('type') == 'accordion'){
          this._handleAccordionKeyDown(e)
        } else {
          this._handleTabsKeyDown(e)
        }
      })

    }

    _handleAccordionKeyDown(e){
      if(e.key == 'ArrowDown' && this.getAttribute('aria-expanded') == 'true'){
        e.preventDefault()
        this.targetElement.focus()
      }
    }

    _handleTabsKeyDown(e){
      if(e.key == 'ArrowRight'){
        e.preventDefault()
        const allTabsInGroup = document.querySelectorAll(`button[group="${this.getAttribute('group')}"]`)
        let nextTogglerIndex

        allTabsInGroup.forEach((toggler, index) => {
          if(toggler == this){
            nextTogglerIndex = index + 1
          }
        })

        if(nextTogglerIndex > allTabsInGroup.length - 1){
          nextTogglerIndex = 0
        }

        allTabsInGroup[nextTogglerIndex].focus()
        allTabsInGroup[nextTogglerIndex]._onTogglerClick(true)
      }

      if(e.key == 'ArrowLeft'){
        e.preventDefault()
        const allTabsInGroup = document.querySelectorAll(`button[group="${this.getAttribute('group')}"]`)
        let nextTogglerIndex
        
        allTabsInGroup.forEach((toggler, index) => {
          if(toggler == this){
            nextTogglerIndex = index - 1
          }
        })

        if(nextTogglerIndex < 0){
          nextTogglerIndex = allTabsInGroup.length - 1
        }

        allTabsInGroup[nextTogglerIndex].focus()
        allTabsInGroup[nextTogglerIndex]._onTogglerClick(true)
      }

      if(e.key == 'Home'){
        e.preventDefault()
        const allTabsInGroup = document.querySelectorAll(`button[group="${this.getAttribute('group')}"]`)
        allTabsInGroup[0].focus()
        allTabsInGroup[0]._onTogglerClick(true)
      }
      if(e.key == 'End'){
        e.preventDefault()
        const allTabsInGroup = document.querySelectorAll(`button[group="${this.getAttribute('group')}"]`)
        allTabsInGroup[allTabsInGroup.length - 1].focus()
        allTabsInGroup[allTabsInGroup.length - 1]._onTogglerClick(true)
      }
    }

    _onTogglerClick(focus){
      document.querySelectorAll(`button[group="${this.getAttribute('group')}"]`).forEach(el => {
        let target = document.querySelector('#' + el.getAttribute('aria-controls'))
        
        if(!target){
          return
        }

        let style = window.getComputedStyle(target)
        if(el == this){

          if(focus){
            // setTimeout(() => {
            //   target.setAttribute('tabindex', - 1)
            //   target.focus()
            // })
            if(this.getAttribute('push-history') === 'true') {
              setTimeout(() => {
                window.history.pushState({ activeTab: this.id }, '', `${window.location.origin}${window.location.pathname}#${this.id}`);
              }, 10);
            }
          }

          //if the current element is `this`, do some extra processing to see if we are showing it or hiding it
          if(style.display === 'none'){
            if(this.getAttribute('type') == 'accordion'){
              this.setAttribute('aria-expanded', true)
            } else {
              this.setAttribute('aria-selected', true)
              this.removeAttribute('tabindex')
            }

            if(this.getAttribute('type') == 'accordion'){
              window.coolHelperFunctions.slideDown(target, this.getAttribute('speed') || 500)
            } else {
              target.style.display = 'block'
            }

            // handle show/hide children
            this.querySelectorAll(`[ada-toggler--expanded]`).forEach(el => el.style.display = "none")
            this.querySelectorAll(`[ada-toggler--collapsed]`).forEach(el => el.style.display = "")
          } else {
            // handle close `this`
            if(this.getAttribute('type') == 'tab'){
              return
            }
            // handle close aria
            if(this.getAttribute('type') == 'accordion'){
              this.setAttribute('aria-expanded', false)
            } else if(this.getAttribute('type') == 'tab') {
              this.setAttribute('aria-selected', false)
              this.setAttribute('tabindex', -1)
            }
            // handle close visual
            if(this.getAttribute('type') == 'accordion'){
              window.coolHelperFunctions.slideUp(target, this.getAttribute('speed') || 500)
            } else {
              target.style.display = 'none'
            }
            // handle show/hide children
            this.querySelectorAll(`[ada-toggler--expanded]`).forEach(el => el.style.display = "")
            this.querySelectorAll(`[ada-toggler--collapsed]`).forEach(el => el.style.display = "none")
          }
        } else {
          //close any element that isn't `this`
          if(this.getAttribute('type') == 'accordion'){
            el.setAttribute('aria-expanded', false)
          } else if(this.getAttribute('type') == 'tab') {
            el.setAttribute('aria-selected', false)
            el.setAttribute('tabindex', -1)
          }
          // handle visual
          if(this.getAttribute('type') == 'accordion'){
            window.coolHelperFunctions.slideUp(target, this.getAttribute('speed') || 500)
          } else {
            target.style.display = 'none'
          }       
        }
      })
    }
  }

  customElements.define('ada-toggler', adaToggler, { extends: 'button' });
}